/**
 * **BugFix 2026-05-23 (Francesco)** — Helper SSOT per il commit di campi
 * di input testuale opzionali nel builder.
 *
 * **Anti-pattern storico** (3 punti corretti: `SectionAspetti`, `SectionRace`,
 * `SectionEquipment`): salvare nel draft il `value.trim()` invece del raw.
 * Conseguenza: digitando "Bl " (spazio finale), `onChange("Bl ")` → save "Bl"
 * → re-render input con `value="Bl"` → spazio sparisce → l'utente non riesce
 * a digitare parole multiple ("non accetta spazi"). Stesso bug per spazi
 * intermedi che vengono tagliati.
 *
 * **Pattern corretto** (questo helper):
 *  - Il **trim() è solo per il check vuoto**: decide se il campo è
 *    "effettivamente compilato" (almeno un carattere non-whitespace).
 *  - Il **valore salvato resta raw** (preservando spazi intermedi e
 *    trailing). Se l'utente vuole "Bl o", deve poterlo scrivere senza che
 *    il sistema gli mangi lo spazio durante typing.
 *
 * **Quando NON usare**: per validation di completezza di sezione (vedi
 * `reducer.ts::isSectionComplete`, `sectionMissingHint.ts`,
 * `moveSetupComplete.ts`) il `value.trim().length === 0` resta legittimo
 * perché legge il valore SENZA modificarlo.
 */

/**
 * Normalizza un input testuale opzionale per il save: ritorna `null` se
 * "effettivamente vuoto" (solo whitespace o stringa vuota), altrimenti
 * il valore **raw** (preserva spazi intermedi e trailing).
 *
 * @param rawValue il `value` corrente dell'input HTML (così come arriva
 *   da `e.target.value` o da state locale)
 * @returns `null` per "non salvare / rimuovi dal draft", oppure il raw
 *   value per "salva così com'è"
 *
 * @example
 *   normalizeOptionalTextInput("")        // → null
 *   normalizeOptionalTextInput("   ")     // → null (solo whitespace)
 *   normalizeOptionalTextInput("Bl o")    // → "Bl o" (raw preservato)
 *   normalizeOptionalTextInput("Bl ")     // → "Bl " (trailing space preservato!)
 *   normalizeOptionalTextInput(" Bl")     // → " Bl" (leading space preservato)
 */
export function normalizeOptionalTextInput(rawValue: string): string | null {
  return rawValue.trim().length === 0 ? null : rawValue;
}

/**
 * Variante semantica: `true` se il valore è "effettivamente vuoto"
 * (whitespace-only o stringa vuota). Per quando il chiamante vuole
 * solo decidere (no need del raw).
 */
export function isOptionalTextInputEmpty(rawValue: string): boolean {
  return rawValue.trim().length === 0;
}
